// dashboard/src/api/app-builds.ts
// Apps Setup → Build tab client. Binary endpoints (keystore/artifact) bypass
// the JSON helper, mirroring push.ts uploadVariantImage.
import { api, KEYSTORE_PENDING_TOAST } from './client';
import { useToastStore } from '../store/toast';
import { useAuthStore } from '../store/auth';
import { API_FETCH_URL } from './base';

const BASE_URL = API_FETCH_URL;

export interface BuildRepo {
  id: number; name: string; owner: string; repo: string; branch: string;
  enabled: number; notes: string | null;
  /** repo_types.id; null = no type yet, its apps cannot build. */
  type_id: number | null;
  /** 1 = outside clients may build from it in App Factory (they see its name and notes). */
  client_visible: number;
}

// Repos Setup tab (api lib/repo-types.ts): per type of base repo, what the
// build needs from an app — 'req' blocks the build without it, 'opt' sends it
// when the app has it, 'off' never sends it.
export type RepoRule = 'req' | 'opt' | 'off';
export const REPO_TYPE_ITEMS = ['firebase', 'admob', 'loader_icon', 'loader_text', 'loader_bg', 'unity_version', 'random_seed', 'domain'] as const;
export type RepoTypeItem = (typeof REPO_TYPE_ITEMS)[number];
export type RepoTypeRules = Record<RepoTypeItem, RepoRule>;
export const REPO_ITEM_LABELS: Record<RepoTypeItem, { name: string; sub: string }> = {
  firebase: { name: 'Firebase', sub: 'google-services.json' },
  admob: { name: 'AdMob', sub: 'app ID + ad unit keys' },
  loader_icon: { name: 'Loader icon', sub: 'transparent PNG on the progress bar' },
  loader_text: { name: 'Loader text', sub: 'line under the progress bar' },
  loader_bg: { name: 'Loader backgrounds', sub: 'portrait 1125×2436 + landscape 2436×1125' },
  unity_version: { name: 'Unity version', sub: 'editor version CI builds with' },
  random_seed: { name: 'Random seed', sub: 'app.random_seed — Required draws one at the first build, Optional sends only one set by hand' },
  domain: { name: 'Domain', sub: 'SDK API host and privacy policy' },
};
export interface RepoTypeUnityVersion {
  version: string; line: string | null; in_draw: boolean;
  /** Apps of this type on it: they block removing it from the type. */
  apps: number;
}
export interface RepoType {
  id: number; name: string; rules: RepoTypeRules; unity_base: string | null;
  /** Newest first. */
  unity_versions: RepoTypeUnityVersion[];
  repos: number;
}
export const getRepoTypes = () => api.get<RepoType[]>('/admin/apps-setup/repo-types');
export const createRepoType = (name: string) => api.post<{ id: number }>('/admin/apps-setup/repo-types', { name });
export const updateRepoType = (id: number, b: { name?: string; rules?: Partial<RepoTypeRules> }) =>
  api.patch<null>(`/admin/apps-setup/repo-types/${id}`, b);
export const deleteRepoType = (id: number) => api.delete<null>(`/admin/apps-setup/repo-types/${id}`);
export const addRepoTypeUnityVersion = (id: number, version: string) =>
  api.post<null>(`/admin/apps-setup/repo-types/${id}/unity-versions`, { version });
export const updateRepoTypeUnityVersion = (id: number, version: string, b: { base?: true; in_draw?: boolean }) =>
  api.patch<null>(`/admin/apps-setup/repo-types/${id}/unity-versions/${encodeURIComponent(version)}`, b);
export const deleteRepoTypeUnityVersion = (id: number, version: string) =>
  api.delete<null>(`/admin/apps-setup/repo-types/${id}/unity-versions/${encodeURIComponent(version)}`);
export const moveRepoTypeUnityVersionToBase = (id: number, version: string) =>
  api.post<{ moved: number }>(`/admin/apps-setup/repo-types/${id}/unity-versions/${encodeURIComponent(version)}/move-to-base`, {});

export interface ReadinessItem {
  key: RepoTypeItem; rule: RepoRule;
  /** later: required, but the build itself provides it (a domain bought, a Unity version drawn). */
  state: 'set' | 'missing' | 'not_set' | 'later' | 'off';
  note?: string;
}
export interface BuildReadiness { ok: boolean; missing: string[]; items?: ReadinessItem[] }
export interface AppBuildConfig {
  app_id: string; base_repo_id: number | null; version_name: string; next_version_code: number;
  /** How the name is written and the most a code grows per build; null until the first build draws them. */
  version_scheme: string | null; version_code_step: number | null;
  /** The next Build-tab build's name; null while the scheme is not drawn. */
  version_preview: string | null;
  keystore_source: 'generated' | 'uploaded' | null; keyalias: string | null; has_keystore: boolean;
}
export interface PlayConsoleVersionInfo {
  connected: boolean;
  package_name: string | null;
  account_name: string | null;
  version_name?: string | null;
  current_version_code?: number | null;
  next_version_code?: number | null;
}
export interface AppBuild {
  id: string; app_id: string; app_name?: string; bundle_id?: string | null;
  build_type: 'apk' | 'aab'; status: string; version_name: string; version_code: number;
  base_repo_id: number | null; base_repo_name: string | null; base_repo_owner: string | null;
  base_repo_repo: string | null; base_repo_branch: string | null;
  gh_run_url: string | null; artifact_r2_key: string | null; play_account_name: string | null;
  error: string | null; created_at: number; updated_at: number;
  // QA verdict — see api/build-qa.ts. `qa_by_name` is only filled by the
  // build-qa endpoints; the plain builds list carries the raw admin id.
  qa_status?: string | null; qa_note?: string | null; qa_by?: string | null;
  qa_at?: number | null; qa_by_name?: string | null;
  // Who started the build (admin id); name/email joined by the builds list.
  requested_by?: string | null; requested_by_name?: string | null; requested_by_email?: string | null;
  // Copied from the app at dispatch; builds older than migration 229 were backfilled to the base.
  unity_version?: string | null;
  // The app's random seed at dispatch (api lib/random-seed.ts); null before migration 247.
  random_seed?: number | null;
}
export interface BuildRepoHistory {
  base_repo_id: number | null; name: string; owner: string | null; repo: string | null;
  branch: string | null; build_count: number; last_used_at: number;
}
export type StartedBuild = Pick<AppBuild, 'id' | 'app_id' | 'build_type' | 'status' | 'version_name' | 'version_code'>;

export const getBuildRepos = () => api.get<BuildRepo[]>('/admin/apps-setup/build-repos');
export const createBuildRepo = (b: { name: string; owner: string; repo: string; branch?: string; type_id: number; notes?: string }) =>
  api.post<null>('/admin/apps-setup/build-repos', b);
export const updateBuildRepo = (id: number, b: Partial<Omit<BuildRepo, 'id'>>) =>
  api.patch<null>(`/admin/apps-setup/build-repos/${id}`, b);
export const deleteBuildRepo = (id: number) => api.delete<null>(`/admin/apps-setup/build-repos/${id}`);

export interface BuildRepoScreenshot {
  id: number; repo_id: number; r2_key: string; url: string;
  content_type: string; position: number; notes: string | null; created_at: number;
}

export const listRepoScreenshots = (repoId: number) =>
  api.get<BuildRepoScreenshot[]>(`/admin/apps-setup/build-repos/${repoId}/screenshots`);

// Raw image body, like the play draft media upload — the JSON helper would
// mangle the bytes, so this mirrors saveBuildConfig's direct fetch.
export async function uploadRepoScreenshot(repoId: number, file: File): Promise<BuildRepoScreenshot> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE_URL}/admin/apps-setup/build-repos/${repoId}/screenshots`, {
    method: 'POST',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const json = await res.json();
  if (!res.ok || !json?.success) throw new Error(json?.error || `Upload failed ${res.status}`);
  return json.data as BuildRepoScreenshot;
}

export const updateRepoScreenshot = (repoId: number, shotId: number, b: { position?: number; notes?: string | null }) =>
  api.patch<null>(`/admin/apps-setup/build-repos/${repoId}/screenshots/${shotId}`, b);

export const deleteRepoScreenshot = (repoId: number, shotId: number) =>
  api.delete<null>(`/admin/apps-setup/build-repos/${repoId}/screenshots/${shotId}`);

export type LoaderKind = 'icon' | 'portrait' | 'landscape';
export interface LoaderImage { url: string; bytes: number | null }
export interface AppLoader {
  icon: LoaderImage | null; portrait: LoaderImage | null; landscape: LoaderImage | null; text: string | null;
}
export const getBuildConfig = (appId: string) =>
  api.get<{
    config: AppBuildConfig; readiness: BuildReadiness; ads?: { enabled: boolean; admob_app_id: string | null };
    unity?: AppUnityStatus;
    random_seed?: AppRandomSeedStatus;
    repo_type?: { id: number; name: string; rules: RepoTypeRules } | null;
    loader?: AppLoader;
    version_schemes?: { id: string; example: string }[];
    version_code_steps?: number[];
  }>(`/admin/apps-setup/builds/config/${appId}`);

// Loader screen of the build; the repo type decides whether CI gets it.
// Images are cropped, scaled and compressed to CI's shape on the server.
export async function uploadLoaderImage(appId: string, kind: LoaderKind, file: File): Promise<{ url: string; bytes: number }> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE_URL}/admin/apps-setup/builds/config/${appId}/loader/${kind}`, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const json = await res.json();
  if (!res.ok || !json?.success) throw new Error(json?.error || `Upload failed ${res.status}`);
  return json.data as { url: string; bytes: number };
}
export const deleteLoaderImage = (appId: string, kind: LoaderKind) =>
  api.delete<null>(`/admin/apps-setup/builds/config/${appId}/loader/${kind}`);
export const saveLoaderText = (appId: string, text: string) =>
  api.put<null>(`/admin/apps-setup/builds/config/${appId}/loader-text`, { text });
/** Draws one part with AI and saves it in place of the current one (api lib/loader-gen.ts). */
export const generateLoaderPart = (
  appId: string, part: 'text' | LoaderKind, body: { brief?: string; with_name?: boolean; motif?: string } = {},
) => api.post<{ text?: string; url?: string; bytes?: number; with_name?: boolean | null; motif?: string }>(
  `/admin/apps-setup/builds/config/${appId}/loader/generate/${part}`, body,
);

// Settings → Unity versions (api lib/unity-versions.ts): the catalog repo
// types pick their versions from; the base is the oldest CI builds with.
export interface UnityVersion {
  version: string; line: string | null; is_lts: number; is_base: number;
  notes: string | null; created_at: number;
  /** Apps (not deleted) that build on it next. */
  apps: number;
  builds: number;
  /** Repo types that list it. */
  types: string[];
}
export type UnityVersionSource = 'random' | 'manual' | 'moved' | 'backfill';
export interface AppUnityOption { version: string; line: string | null; is_lts: boolean; is_base: boolean; in_draw: boolean }
export interface AppUnityStatus {
  version: string | null; line: string | null; is_base: boolean; is_lts: boolean; in_draw: boolean;
  /** False when the app's repo type does not list its version. */
  on_type: boolean;
  source: UnityVersionSource | null; assigned_at: number | null; builds_with_it: number;
  type: { id: number; name: string; rule: RepoRule } | null;
  /** The versions of the app's repo type, newest first: the ones it may switch to. */
  options: AppUnityOption[];
}
export const getUnityVersions = () => api.get<UnityVersion[]>('/admin/apps-setup/unity-versions');
export const addUnityVersion = (b: { version: string; is_lts?: boolean; notes?: string }) =>
  api.post<null>('/admin/apps-setup/unity-versions', b);
export const updateUnityVersion = (version: string, b: { is_lts?: boolean; notes?: string | null }) =>
  api.patch<null>(`/admin/apps-setup/unity-versions/${encodeURIComponent(version)}`, b);
export const deleteUnityVersion = (version: string) =>
  api.delete<null>(`/admin/apps-setup/unity-versions/${encodeURIComponent(version)}`);
export const setAppUnityVersion = (appId: string, version: string) =>
  api.put<AppUnityStatus>(`/admin/apps-setup/builds/config/${appId}/unity-version`, { version });

// The app's random seed (api lib/random-seed.ts), sent to CI as app.random_seed.
export const RANDOM_SEED_MAX = 2147483647;
export interface AppRandomSeedStatus {
  /** Null until the first build draws one. */
  seed: number | null;
  source: 'random' | 'manual' | null; assigned_at: number | null; builds_with_it: number;
}
/** A seed = set by hand; null = draw a new one. */
export const setAppRandomSeed = (appId: string, seed: number | null) =>
  api.put<AppRandomSeedStatus>(`/admin/apps-setup/builds/config/${appId}/random-seed`, { seed });

/** "Unity 6.4", "Unity 6.3 LTS · base" — the small line under a version. */
export function unityLineLabel(v: { line: string | null; is_lts: boolean | number; is_base: boolean | number }): string {
  return `Unity ${v.line ?? '?'}${v.is_lts ? ' LTS' : ''}${v.is_base ? ' · base' : ''}`;
}

export const getPlayConsoleVersion = (appId: string) =>
  api.get<PlayConsoleVersionInfo>(`/admin/apps-setup/builds/console-version/${appId}`);

export async function saveBuildConfig(
  appId: string,
  fields: { base_repo_id?: number; version_name?: string; next_version_code?: number; version_scheme?: string; version_code_step?: number; keyalias?: string; keystore_pass?: string; keyalias_pass?: string },
  keystoreFile?: File,
): Promise<{ pending_approval?: true }> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== '') form.append(k, String(v));
  if (keystoreFile) form.append('keystore', keystoreFile);
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE_URL}/admin/apps-setup/builds/config/${appId}`, {
    method: 'PUT',
    body: form,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const json = await res.json();
  if (!res.ok || !json?.success) throw new Error(json?.error || `Save failed ${res.status}`);
  // Uploading over an existing keystore may come back as a filed approval
  // request instead: nothing was saved.
  if (json.data?.pending_approval) {
    useToastStore.getState().push(KEYSTORE_PENDING_TOAST);
    return { pending_approval: true };
  }
  return {};
}

/** Regenerate answers with a filed approval request for anyone but a super admin. */
export const generateKeystore = (appId: string, force = false) =>
  api.post<{ alias: string; pending_approval?: undefined } | { pending_approval: true }>(
    `/admin/apps-setup/builds/config/${appId}/generate-keystore`, { force });

export const getSigning = (appId: string) =>
  api.get<{ keyalias: string | null; keystore_pass: string; keyalias_pass: string | null }>(
    `/admin/apps-setup/builds/config/${appId}/signing`);

async function downloadAuthed(path: string, filename: string): Promise<void> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    let msg = `Download failed ${res.status}`;
    try { msg = (await res.json())?.error || msg; } catch { /* binary or empty */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export const downloadKeystore = (appId: string) =>
  downloadAuthed(`/admin/apps-setup/builds/config/${appId}/keystore`, 'user.keystore');

// version_source 'store' = the next version after Play's production release (random step, same for the whole update)
// (Update Batches); omitted = the Build tab's per-app counter.
/** `batch_id`: started from an Update Batch row — the batch counts only its own builds. */
export const startBuild = (appId: string, build_type: 'apk' | 'aab', opts: { version_source?: 'store'; batch_id?: number } = {}) =>
  api.post<StartedBuild>(`/admin/apps-setup/builds/${appId}/start`, { build_type, ...opts });

/** Stop a dispatched/running build. `warning`: the row is stopped but the
 *  GitHub run could not be cancelled (it will fail on its own). */
export const cancelBuild = (buildId: string) =>
  api.post<{ warning: string | null }>(`/admin/apps-setup/builds/${encodeURIComponent(buildId)}/cancel`, {});

export interface RepoCommit { sha: string; message: string; author: string | null; date: string | null; html_url: string; }

/** Before an AAB. QA checks a repo, not a bundle: `qa` is the newest verdict
 *  on any APK from this app's base repo and branch, and `state` colours the
 *  Build AAB button (api lib/aab-qa.ts). */
export type AabQaState = 'safe' | 'unchecked' | 'failed';
export interface AabCheck {
  state: AabQaState;
  /** This app's newest APK. */
  apk: {
    id: string; version_name: string; version_code: number; created_at: number;
    /** `commit_approx`: an older build with no recorded commit — this is the branch head at its dispatch time. */
    commit_sha: string | null; commit_approx: boolean;
  } | null;
  qa: {
    build_id: string; app_id: string; bundle_id: string | null;
    this_app: boolean; is_last_apk: boolean;
    status: 'passed' | 'failed'; note: string | null; at: number | null; by_name: string | null;
    commit_sha: string | null; commit_approx: boolean;
    /** A pass the repo has moved past (or whose commit is unknown): shown as Not checked. */
    stale: boolean;
  } | null;
  repo: {
    id: number; name: string; owner: string; repo: string; branch: string | null;
    same_as_apk: boolean;
    head: RepoCommit | null;
    /** What `ahead_by` counts from; null = nothing to count from. */
    since: 'checked' | 'failed' | 'last_apk' | null;
    ahead_by: number | null;
    compare_url: string | null;
    error: string | null;
  } | null;
}

export const getAabCheck = (appId: string) =>
  api.get<AabCheck>(`/admin/apps-setup/builds/aab-check/${encodeURIComponent(appId)}`);

export const getBuilds = (appId?: string) =>
  api.get<AppBuild[]>(`/admin/apps-setup/builds${appId ? `?app_id=${encodeURIComponent(appId)}` : ''}`);

export const getBuildRepoHistory = (appId: string) =>
  api.get<BuildRepoHistory[]>(`/admin/apps-setup/builds/repo-history/${encodeURIComponent(appId)}`);

// Asks the API for a short-lived direct link and sends the browser there, so
// the 60–70 MB file goes through the browser's download manager instead of a
// fetch()+blob() in JS, which failed intermittently with "Failed to fetch".
export async function downloadArtifact(b: Pick<AppBuild, 'id'>): Promise<void> {
  const { url } = await api.post<{ url: string; filename: string; expires_in: number }>(
    `/admin/apps-setup/builds/${b.id}/artifact-link`, {});
  window.location.assign(url);
}

// The obfuscation decode map the CI run uploads next to the build; small
// enough for fetch()+blob().
export const downloadObfsMapping = (b: Pick<AppBuild, 'id'>) =>
  downloadAuthed(`/admin/apps-setup/builds/${b.id}/obfs-mapping`, 'ObfsDecodeMapping.json');

// The app's own domain (api lib/app-domains.ts): bought at the first build,
// carries the app's tracker links and the SDK's API host.
export type AppDomainState = 'new' | 'registering' | 'registered' | 'zone' | 'attached' | 'linked' | 'ready';
export interface AppDomain {
  app_id: string; hostname: string | null; state: AppDomainState;
  price: string | null; renewal_price: string | null; currency: string | null;
  expires_at: string | null; auto_renew: number | null; error: string | null;
  created_at: number; updated_at: number;
}
export interface AppDomainStatus {
  domain: AppDomain | null;
  // Why nothing can be bought right now (no token / turned off), or null.
  unavailable: string | null;
  // The app was built before it had a domain: nothing buys one for it but "Buy now".
  built_before: boolean;
  // The app's policy link (its AdMob import's, else its own): on the domain once it is registered, else the generator's.
  privacy_policy_url: string | null;
  // The contact the policy page shows; also from the import first, then the app.
  contact_email: string | null;
}
export const getAppDomain = (appId: string) =>
  api.get<AppDomainStatus>(`/admin/apps-setup/builds/domain/${appId}`);
export const buyAppDomain = (appId: string) =>
  api.post<AppDomainStatus>(`/admin/apps-setup/builds/domain/${appId}/buy`, {});
export const retryAppDomain = (appId: string) =>
  api.post<AppDomainStatus>(`/admin/apps-setup/builds/domain/${appId}/retry`, {});
// Sets the contact email and (re)mints the privacy policy, AdMob import or not.
export const saveAppPrivacyPolicy = (appId: string, email: string) =>
  api.post<AppDomainStatus>(`/admin/apps-setup/builds/privacy-policy/${appId}`, { email });

// SHA-1 / SHA-256 of the keystore's signing certificate (keytool -list -v spelling).
export interface KeystoreFingerprint { alias: string | null; subject: string | null; sha1: string; sha256: string }
export const getKeystoreFingerprint = (appId: string) =>
  api.get<KeystoreFingerprint>(`/admin/apps-setup/builds/config/${appId}/fingerprint`);
